import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface UpcomingLesson {
  id: string
  relationshipId: string
  instructorId: string
  instructorName: string | null
  scheduledAt: string
  plannedEndAt: string | null
}

export interface MyRequest {
  id: string
  kind: 'new_booking' | 'reschedule'
  relationshipId: string
  lessonId: string | null
  instructorId: string
  instructorName: string | null
  requestedStart: string
  requestedEnd: string
}

interface ActionResult {
  ok: boolean
  code?: string
}

/**
 * The student's own calendar (migration 094): their upcoming confirmed lessons and their
 * own still-pending requests (new booking or reschedule), with the actions to request a
 * lesson, cancel immediately (no instructor confirmation needed), request a reschedule
 * (needs instructor confirmation), and withdraw a pending request.
 */
export function useStudentCalendar() {
  const { user } = useAuth()
  const [lessons, setLessons] = useState<UpcomingLesson[]>([])
  const [requests, setRequests] = useState<MyRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const fetchAll = useCallback(async () => {
    if (!user) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const [lessonsRes, requestsRes] = await Promise.all([
        supabase.from('driving_lessons')
          .select('id, relationship_id, instructor_id, scheduled_at, planned_end_at')
          .eq('student_id', user.id)
          .eq('status', 'scheduled')
          .is('finished_at', null)
          .order('scheduled_at', { ascending: true }),
        supabase.from('lesson_booking_requests')
          .select('id, kind, relationship_id, lesson_id, instructor_id, requested_start, requested_end')
          .eq('student_id', user.id)
          .eq('status', 'pending')
          .order('requested_start', { ascending: true }),
      ])

      if (lessonsRes.error || requestsRes.error) { setError('Could not load your calendar.'); return }

      const instructorIds = [...new Set([
        ...(lessonsRes.data ?? []).map(l => l.instructor_id),
        ...(requestsRes.data ?? []).map(r => r.instructor_id),
      ])]
      const { data: profiles } = instructorIds.length
        ? await supabase.from('profiles').select('id, full_name').in('id', instructorIds)
        : { data: [] }
      const nameById = new Map((profiles ?? []).map(p => [p.id, p.full_name]))

      setLessons((lessonsRes.data ?? []).map(l => ({
        id: l.id, relationshipId: l.relationship_id, instructorId: l.instructor_id,
        instructorName: nameById.get(l.instructor_id) ?? null,
        scheduledAt: l.scheduled_at, plannedEndAt: l.planned_end_at,
      })))
      setRequests((requestsRes.data ?? []).map(r => ({
        id: r.id, kind: r.kind as 'new_booking' | 'reschedule', relationshipId: r.relationship_id,
        lessonId: r.lesson_id, instructorId: r.instructor_id,
        instructorName: nameById.get(r.instructor_id) ?? null,
        requestedStart: r.requested_start, requestedEnd: r.requested_end,
      })))
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => { fetchAll() }, [fetchAll])

  const requestLesson = useCallback(async (fields: {
    relationshipId: string; startAt: string; endAt: string; notes?: string | null
  }): Promise<ActionResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('book-lesson', {
        body: { relationshipId: fields.relationshipId, startAt: fields.startAt, endAt: fields.endAt, notes: fields.notes ?? null },
      })
      if (fnError) return { ok: false, code: 'internal_error' }
      const result = data as ActionResult
      if (result.ok) await fetchAll()
      return result
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  const cancelLesson = useCallback(async (lessonId: string, reason?: string | null): Promise<ActionResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('cancel-lesson', {
        body: { lessonId, reason: reason ?? null },
      })
      if (fnError) return { ok: false, code: 'internal_error' }
      const result = data as ActionResult
      if (result.ok) await fetchAll()
      return result
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  const requestReschedule = useCallback(async (lessonId: string, newStartAt: string, newEndAt: string): Promise<ActionResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('request-reschedule', {
        body: { lessonId, newStartAt, newEndAt },
      })
      if (fnError) return { ok: false, code: 'internal_error' }
      const result = data as ActionResult
      if (result.ok) await fetchAll()
      return result
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  const withdrawRequest = useCallback(async (requestId: string): Promise<ActionResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('withdraw_booking_request', { p_request_id: requestId })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as unknown as ActionResult
      if (result.ok) await fetchAll()
      return result
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  return {
    lessons, requests, loading, error, saving,
    requestLesson, cancelLesson, requestReschedule, withdrawRequest, refresh: fetchAll,
  }
}
